import React, { useEffect, useCallback, useState } from 'react';
import { Box, Typography, Alert, CircularProgress, Button } from '@mui/material';
import { useAuth } from '../context/AuthContext';
import { useTenant } from '../context/TenantContext';
import { useNavigate, useLocation } from 'react-router-dom';
import Logo from './Logo';

export default function Login() {
  const { handleGoogleLogin, isAuthenticated } = useAuth();
  const { tenantAlias, tenantInfo, isLoading: tenantLoading, error: tenantError } = useTenant();
  const [error, setError] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  // Check if user is already authenticated
  useEffect(() => {
    if (isAuthenticated()) {
      navigate(location.state?.from?.pathname || "/");
    }
  }, [isAuthenticated, navigate, location]);

  // Initialize Google Sign-In
  const initializeGoogleSignIn = useCallback(() => {
    if (!window.google || !tenantInfo || !tenantInfo.auth) return;
    
    const clientId = tenantInfo.auth.config.client_id;
    
    window.google.accounts.id.initialize({
      client_id: clientId,
      callback: async (response) => {
        try {
          setIsLoading(true);
          setError(null);
          const success = await handleGoogleLogin(response);
          if (!success) {
            setError('Login failed. Please try again.');
          }
        } catch (error) {
          console.error('Login error:', error);
          // Extract user-friendly error message
          let errorMessage = 'Authentication failed. Please try again.';
          if (error.response) {
            switch (error.response.status) {
              case 404:
                errorMessage = 'User not found in this tenant. Please contact your administrator.';
                break;
              case 403:
                errorMessage = 'You do not have access to this tenant. Please contact your administrator.';
                break;
              case 401:
                errorMessage = 'Your session has expired. Please try again.';
                break;
              default:
                if (error.response.data?.message) {
                  errorMessage = error.response.data.message;
                }
            }
          }
          setError(errorMessage);
        } finally {
          setIsLoading(false);
        }
      }
    });

    window.google.accounts.id.renderButton(
      document.getElementById("googleSignInButton"),
      { theme: "outline", size: "large" }
    );
  }, [handleGoogleLogin, tenantInfo]);

  // Load Google Sign-In script
  useEffect(() => {
    if (!tenantInfo || !tenantInfo.auth || tenantInfo.auth.provider !== 'GOOGLE') return;
    
    const script = document.createElement('script');
    script.src = 'https://accounts.google.com/gsi/client';
    script.async = true;
    script.defer = true;
    script.onload = initializeGoogleSignIn;
    document.body.appendChild(script);
    
    return () => {
      if (document.body.contains(script)) {
        document.body.removeChild(script);
      }
    };
  }, [initializeGoogleSignIn, tenantInfo]);

  // Show loading state
  if (tenantLoading) {
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: '100vh'
        }}
      >
        <CircularProgress size={60} />
        <Typography variant="h6" sx={{ mt: 2 }}>
          Loading tenant configuration...
        </Typography>
      </Box>
    );
  }

  // Show tenant error
  if (tenantError) {
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: '100vh'
        }}
      >
        <Typography variant="h4" gutterBottom>
          Welcome to Supply Chain OS
        </Typography>
        <Alert severity="error" sx={{ mb: 3, maxWidth: 600 }}>
          {tenantError}
        </Alert>
        <Button 
          variant="contained" 
          onClick={() => window.location.reload()}
        >
          Retry
        </Button>
      </Box>
    );
  }

  // Show auth error
  if (error) {
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: '100vh'
        }}
      >
        <Typography variant="h4" gutterBottom>
          Welcome to Supply Chain OS
        </Typography>
        <Alert severity="error" sx={{ mb: 3, maxWidth: 600 }}>
          {error}
        </Alert>
        <Button 
          variant="contained" 
          onClick={() => {
            setError(null);
            window.location.reload();
          }}
        >
          Try Again
        </Button>
      </Box>
    );
  }

  // Render login options based on auth configuration
  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '100vh'
      }}
    >
      <Typography variant="h4" gutterBottom>
        Welcome to Supply Chain OS
      </Typography>
      
      {tenantInfo && (
        <>
          <Box sx={{ mb: 4, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
            <Logo size="large" />
            <Typography variant="subtitle1" sx={{ mt: 2 }}>
              Tenant: {tenantInfo.tenant_name || tenantAlias}
            </Typography>
          </Box>
        </>
      )}

      {tenantInfo && tenantInfo.auth && (
        <Box sx={{ my: 4 }}>
          {tenantInfo.auth.provider === 'GOOGLE' && (
            <>
              {isLoading && (
                <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}>
                  <CircularProgress size={24} />
                  <Typography variant="body2" sx={{ ml: 2 }}>
                    Authenticating...
                  </Typography>
                </Box>
              )}
              <div id="googleSignInButton" />
            </>
          )}
          
          {tenantInfo.auth.provider !== 'GOOGLE' && (
            <Alert severity="info" sx={{ mb: 3, maxWidth: 600 }}>
              Authentication provider "{tenantInfo.auth.provider}" is not yet supported in the frontend.
            </Alert>
          )}
        </Box>
      )}
    </Box>
  );
}
